import { useEffect } from "react";
import { Link2Icon, TrashIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";

// Permalink support for thread posts (bug/prd): each post card carries an
// anchor id, the header gets a copy-link button, and opening a #post-… URL
// scrolls to the post once the async post list has rendered.

export const postAnchorId = (id: string | number) => `post-${id}`;

export const CopyPostLinkButton = ({ postId }: { postId: string | number }) => {
  const { toast } = useToast();
  const copy = () => {
    const url = `${window.location.origin}${window.location.pathname}#${postAnchorId(postId)}`;
    navigator.clipboard.writeText(url).then(
      () => toast({ title: "Link copied", description: url }),
      () => toast({ variant: "destructive", title: "Copy failed" })
    );
  };
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={copy}
      className="ml-auto h-6 w-6 shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100"
      aria-label="Copy link to post"
      title="Copy link"
    >
      <Link2Icon className="h-3.5 w-3.5" />
    </Button>
  );
};

// Delete with confirmation — thread posts are shared discussion history, a
// stray hover-click must not wipe one silently.
export const DeletePostButton = ({ onDelete }: { onDelete: () => void }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>
      <Button
        variant="ghost"
        size="icon"
        className="h-6 w-6 shrink-0 text-muted-foreground opacity-0 hover:text-destructive group-hover:opacity-100"
        aria-label="Delete post"
      >
        <TrashIcon className="h-3.5 w-3.5" />
      </Button>
    </AlertDialogTrigger>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Delete this post?</AlertDialogTitle>
        <AlertDialogDescription>
          The post is removed from the thread for everyone. This cannot be
          undone.
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>Cancel</AlertDialogCancel>
        <AlertDialogAction
          onClick={onDelete}
          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
        >
          Delete
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

// Browsers only auto-scroll to anchors present at load; posts arrive async,
// so re-run the jump once the list has rendered.
export const useScrollToHashPost = (ready: boolean) => {
  useEffect(() => {
    if (!ready) return;
    const hash = window.location.hash;
    if (!hash.startsWith("#post-")) return;
    document
      .getElementById(hash.slice(1))
      ?.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [ready]);
};

// Card styling shared by both threads: anchor offset + highlight when the
// URL hash targets this post.
export const POST_CARD_CLASS =
  "group scroll-mt-16 rounded-md border target:border-primary target:bg-primary/5";
